import { Link } from "react-router-dom";
import { useClassrooms } from "../data/ClassroomContext";
import { useClassroomLearners, useClassroomOverview } from "../data/useClassroomWorkspace";
import { CheckIcon } from "../components/Icons";
import { Message } from "../components/Status";
import WorkspaceStatus from "../components/WorkspaceStatus";
import { STATE, STATE_ORDER, StatePill, StepProgress } from "../components/WorkState";
import ui from "../components/ui.module.css";

const plural = (count, one, many = `${one}s`) => `${count} ${count === 1 ? one : many}`;

// The one thing that needs the teacher: completed work nobody has reviewed.
const NeedsReview = ({ count }) =>
  count > 0 ? (
    <section className={`${ui.card} ${ui.featured} ${ui.row}`} aria-labelledby="needs-review">
      <h2 id="needs-review" className={ui.cardTitle}>
        {plural(count, "piece")} of work to review
      </h2>
      <Link to="/work" className={`${ui.button} ${ui.primary}`}>
        Review work
      </Link>
    </section>
  ) : (
    <p className={`${ui.state} ${ui.done}`}>
      <CheckIcon size={16} />
      No work waiting for review
    </p>
  );

const StateCounts = ({ learners }) => {
  const counts = learners.reduce(
    (all, { state }) => ({ ...all, [state]: (all[state] || 0) + 1 }),
    {}
  );
  return (
    <ul className={ui.countRow} aria-label="Learners by state">
      {STATE_ORDER.map((state) => (
        <li key={state} className={`${ui.countChip} ${ui[`state_${state}`] || ""}`}>
          <strong>{counts[state] || 0}</strong> {STATE[state]}
        </li>
      ))}
    </ul>
  );
};

const countsText = ({ notStarted, working, completed, reviewed }) =>
  [
    working && `${working} working`,
    completed && `${completed} completed`,
    reviewed && `${reviewed} reviewed`,
    notStarted && `${notStarted} not started`,
  ]
    .filter(Boolean)
    .join(" · ");

// What the class has been sent recently, and how far along it is.
const RecentWork = ({ activities }) =>
  activities.length > 0 && (
    <section className={ui.stack} aria-labelledby="recent-work">
      <h2 id="recent-work" className={ui.eyebrow}>
        Recent work
      </h2>
      <ul className={ui.list}>
        {activities.slice(0, 3).map((item) => (
          <li key={item.releaseId} className={`${ui.card} ${ui.compactCard}`}>
            <p className={ui.learnerName}>{item.activity.title}</p>
            <p className={ui.meta}>{countsText(item.counts)}</p>
          </li>
        ))}
      </ul>
    </section>
  );

export const LearnerCard = ({ learner }) => (
  <Link
    to={`/learners/${learner.id}`}
    className={`${ui.learnerCard} ${ui[`state_${learner.state}`] || ""}`}
  >
    <span className={ui.learnerCardTop}>
      <span className={ui.learnerName}>{learner.name}</span>
      <StatePill state={learner.state} />
    </span>
    <StepProgress completedSteps={learner.completedSteps} totalSteps={learner.totalSteps} />
  </Link>
);

const Learners = () => {
  const { active } = useClassrooms();
  const overview = useClassroomOverview(active.id);
  const roster = useClassroomLearners(active.id);
  const learners = roster.data?.learners || [];
  const count = overview.data?.enrolledLearnerCount ?? (roster.data ? learners.length : null);

  return (
    <div className={ui.page}>
      <div className={ui.stack}>
        <h1 className={ui.title}>Learners</h1>
        <p className={ui.eyebrow}>
          {[active.name, count != null && plural(count, "learner")].filter(Boolean).join(" · ")}
        </p>
      </div>

      {overview.data && <NeedsReview count={overview.data.needsReviewCount} />}

      <WorkspaceStatus resource={roster} loadingLabel="Loading your learners…" className={active.name} />

      {roster.data &&
        (learners.length ? (
          <>
            <StateCounts learners={learners} />
            <section className={ui.stack} aria-labelledby="class-learners">
              <h2 id="class-learners" className={ui.visuallyHidden}>
                Learners in {active.name}
              </h2>
              <ul className={ui.learnerGrid}>
                {learners.map((learner) => (
                  <li key={learner.id}>
                    <LearnerCard learner={learner} />
                  </li>
                ))}
              </ul>
            </section>
          </>
        ) : (
          <Message title="No learners yet">
            Learners appear here once your school enrols them in {active.name}.
          </Message>
        ))}

      {overview.data && <RecentWork activities={overview.data.recentActivities || []} />}
    </div>
  );
};

export default Learners;
